import type { TReviewLineItem } from "./createViewFileReview";
import type { TSearchableOption } from "@/ui/molecules/SearchableSelect";

/** Subset of line item field metadata used for client-side validation. */
export type TLineItemFieldDefForValidation = {
    key: keyof TReviewLineItem;
    label: string;
    required?: boolean;
    alwaysReadOnly?: boolean;
    readOnlyWhenHasValue?: boolean;
};

export type TValidateLineEditFormParams = {
    form: Partial<TReviewLineItem>;
    fields: TLineItemFieldDefForValidation[];
};

/**
 * Client-side validation for the file review line edit modal.
 * Mirrors a subset of backend rules (required mapped account/propcode).
 */
export function validateLineEditForm(
    params: TValidateLineEditFormParams,
): Partial<Record<keyof TReviewLineItem, string>> {
    const errors: Partial<Record<keyof TReviewLineItem, string>> = {};
    const { form, fields } = params;

    for (const field of fields) {
        if (!field.required || field.alwaysReadOnly) continue;
        const val = form[field.key];
        if (val === null || val === undefined || String(val).trim() === "") {
            errors[field.key] = `${field.label} is required`;
        }
    }

    // Note: propcode is selection-only via SearchableSelect; no "valid propcode" list
    // check is needed here — only the required check above applies.

    return errors;
}

/** Strip optional `CODE: message` wrapper (some paths may still include it). */
function normalizeBackendLineMessage(raw: string): string {
    let t = raw.trim();
    const codeMatch = t.match(/^[A-Z][A-Z0-9_]+:\s*(.+)$/s);
    if (codeMatch) t = codeMatch[1]!.trim();
    return t;
}

/**
 * Maps backend line validation messages to edit-modal field keys so invalid rows
 * highlight the same areas as the table tooltip. Covers `FileValidationService` and
 * OTB row validators.
 */
export function mapBackendLineItemValidationErrorsToFields(
    validationErrors: string[],
): Partial<Record<keyof TReviewLineItem, string>> {
    const out: Partial<Record<keyof TReviewLineItem, string>> = {};

    const setMsg = (key: keyof TReviewLineItem, msg: string) => {
        const prev = out[key];
        out[key] = prev && prev !== msg ? `${prev}; ${msg}` : msg;
    };

    for (const raw of validationErrors) {
        const msg = normalizeBackendLineMessage(raw);
        if (!msg) continue;

        if (
            /row must have debit\/credit or amount value/i.test(msg) ||
            /^AMOUNT is required/i.test(msg)
        ) {
            setMsg("amount", msg);
            continue;
        }

        if (/debit amount cannot be negative/i.test(msg)) {
            setMsg("debit", msg);
            continue;
        }

        if (/credit amount cannot be negative/i.test(msg)) {
            setMsg("credit", msg);
            continue;
        }

        if (/account number is required/i.test(msg)) {
            setMsg("breAccountNumber", msg);
            continue;
        }

        if (/^propcode is required/i.test(msg)) {
            setMsg("propcode", msg);
            continue;
        }

        if (/SOURCE_ACCOUNT_NUMBER is required/i.test(msg)) {
            setMsg("sourceAccountNumber", msg);
            continue;
        }

        if (/LOCAL_PROPERTY_CODE is required/i.test(msg)) {
            setMsg("localPropcode", msg);
            continue;
        }

        if (/property code not mapped|unmapped property/i.test(msg)) {
            setMsg("propcode", msg);
            setMsg("localPropcode", msg);
        }
    }

    return out;
}

export function mergeLineEditFieldErrors(params: {
    server: Partial<Record<keyof TReviewLineItem, string>>;
    client: Partial<Record<keyof TReviewLineItem, string>>;
    showClientErrors: boolean;
    editingLineItem: TReviewLineItem | null;
    editFormData: Partial<TReviewLineItem>;
}): Partial<Record<keyof TReviewLineItem, string>> {
    const { server, client, showClientErrors, editingLineItem, editFormData } = params;
    const merged: Partial<Record<keyof TReviewLineItem, string>> = { ...server };

    if (editingLineItem) {
        for (const k of Object.keys(merged) as (keyof TReviewLineItem)[]) {
            const init = editingLineItem[k];
            const cur = editFormData[k];
            if (String(init ?? "") !== String(cur ?? "")) {
                delete merged[k];
            }
        }
    }

    if (showClientErrors) {
        for (const k of Object.keys(client) as (keyof TReviewLineItem)[]) {
            const v = client[k];
            if (v) merged[k] = v;
        }
    }

    return merged;
}

/**
 * Apply a regular (non-account) field change to the form.
 */
export function applyLineItemFieldChange(
    prev: Partial<TReviewLineItem>,
    key: keyof TReviewLineItem,
    value: string | number,
): Partial<TReviewLineItem> {
    return { ...prev, [key]: value };
}

/**
 * Apply an account selection (from either the code or name SearchableSelect) to the form.
 * Selecting from either field sets BOTH breAccountNumber and breAccountName.
 * Passing null clears both fields.
 */
export function applyAccountSelection(
    prev: Partial<TReviewLineItem>,
    option: TSearchableOption | null,
): Partial<TReviewLineItem> {
    if (option === null) {
        return { ...prev, breAccountNumber: null, breAccountName: null };
    }
    const code = String(option["code"] ?? option.value);
    const name = String(option["name"] ?? option.label);
    return { ...prev, breAccountNumber: code, breAccountName: name };
}
